import { useEffect, useState } from "react";
/* eslint-disable react/no-unescaped-entities */
import {
    AlertTriangle,
    Briefcase,
    CheckCircle2,
    ChevronDown,
    ChevronUp,
    ClipboardList,
    FilePlus,
    GitBranch,
    HelpCircle,
    Lightbulb,
    Loader2,
    Sparkles,
    Star,
    Trash2,
} from "lucide-react";
import { cockpit } from "@/lib/cockpitAuth";

// ─────────────────────────────────────────────────────────────
// Module catalogue — labels + icons + descriptions
// ─────────────────────────────────────────────────────────────
const MODULE_META = {
    opportunities: {
        label: "Opportunités en étude",
        icon: Lightbulb,
        description:
            "Projets analysés en parallèle de la mission — montre que XV " +
            "travaille en permanence sur le développement.",
    },
    committee: {
        label: "Comité d'investissement XV",
        icon: Star,
        description:
            "Notes stratégiques formelles type SWOT + recommandation, " +
            "présentées comme une analyse de comité interne.",
    },
    alerts: {
        label: "Alertes stratégiques",
        icon: AlertTriangle,
        description:
            "Signaux vert / orange / rouge sur les risques et points " +
            "d'attention identifiés par XV.",
    },
    questions: {
        label: "Questions stratégiques à l'étude",
        icon: HelpCircle,
        description:
            "Arbitrages en cours (equity, dette, SPV…) avec analyse XV — " +
            "le client perçoit les réflexions en cours.",
    },
    value_added: {
        label: "Valeur ajoutée créée par XV",
        icon: CheckCircle2,
        description:
            "Journal des apports concrets de XV (partenaires identifiés, " +
            "risques évités, structurations) — justifie les honoraires.",
    },
    documents: {
        label: "Documents attendus",
        icon: ClipboardList,
        description:
            "Checklist documents reçus / attendus — le client visualise " +
            "immédiatement ce qui bloque l'avancement.",
    },
    pipeline: {
        label: "Pipeline stratégique",
        icon: GitBranch,
        description:
            "Timeline simple (Idée → Closing) — l'étape courante est " +
            "mise en évidence visuellement.",
    },
};

const MODULE_ORDER = [
    "opportunities", "committee", "alerts", "questions",
    "value_added", "documents", "pipeline",
];

const DECISION_OPTIONS_OP = [
    { v: "en_etude", l: "En étude" },
    { v: "go", l: "GO" },
    { v: "go_conditions", l: "GO sous conditions" },
    { v: "no_go", l: "NO GO" },
    { v: "suspendu", l: "Suspendu" },
];
const DECISION_OPTIONS_COMITE = [
    { v: "", l: "—" },
    { v: "go", l: "GO" },
    { v: "go_conditions", l: "GO sous conditions" },
    { v: "no_go", l: "NO GO" },
];
const PRIORITY_OPTIONS = [
    { v: "", l: "—" },
    { v: "low", l: "Basse" },
    { v: "medium", l: "Moyenne" },
    { v: "high", l: "Haute" },
];
const ALERT_LEVELS = [
    { v: "green", l: "Vert", color: "#7fb069" },
    { v: "orange", l: "Orange", color: "#e6a23c" },
    { v: "red", l: "Rouge", color: "#e89090" },
];
const VALUE_CATEGORIES = [
    { v: "partner", l: "Identification d'un partenaire" },
    { v: "risk", l: "Risque évité" },
    { v: "financing", l: "Piste de financement" },
    { v: "structuration", l: "Proposition de structuration" },
    { v: "legal", l: "Optimisation juridique" },
    { v: "relation", l: "Mise en relation qualifiée" },
    { v: "other", l: "Autre" },
];
const DOC_STATUSES = [
    { v: "received", l: "Reçu", color: "#7fb069" },
    { v: "expected", l: "Attendu", color: "#e6a23c" },
];

// ─────────────────────────────────────────────────────────────
// Top-level panel — toggles + each enabled module's CRUD UI
// ─────────────────────────────────────────────────────────────
export default function CockpitDossierModulesPanel({ dossierId }) {
    const [state, setState] = useState(null);
    const [busy, setBusy] = useState(false);

    const reload = async () => {
        const { data } = await cockpit.get(`/dossiers/${dossierId}/modules`);
        setState(data);
    };

    useEffect(() => {
        reload();
    }, [dossierId]);

    const toggle = async (key) => {
        if (!state) return;
        setBusy(true);
        const next = {
            ...state.modules_enabled,
            [key]: !state.modules_enabled[key],
        };
        try {
            await cockpit.put(`/dossiers/${dossierId}/modules`, {
                modules_enabled: next,
            });
            await reload();
        } finally {
            setBusy(false);
        }
    };

    if (!state) return null;

    return (
        <section
            data-testid="cockpit-modules-panel"
            className="mt-16 border-t border-white/[0.06] pt-12"
        >
            <div className="overline text-[#d4af37]/70 mb-3">
                Investment Office XV
            </div>
            <h2
                className="font-serif text-[26px] sm:text-[30px] text-[#f4ecd8] mb-4"
                style={{ fontWeight: 400 }}
            >
                Modules premium activables
            </h2>
            <p className="text-[13px] text-[#f4ecd8]/55 leading-[1.85] font-light mb-10 max-w-3xl italic">
                Activez les modules pour transformer ce dossier en suivi
                d'Investment Office. Chaque module activé apparaît côté
                client de manière sobre et discrète.
            </p>

            {/* Toggles grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
                {MODULE_ORDER.map((key) => {
                    const meta = MODULE_META[key];
                    const Icon = meta.icon;
                    const active = !!state.modules_enabled[key];
                    const count = state.counts?.[key] || 0;
                    return (
                        <label
                            key={key}
                            data-testid={`module-toggle-${key}`}
                            className={`relative cursor-pointer border p-5 transition-all duration-500 ${
                                active
                                    ? "border-[#d4af37]/60 bg-[#d4af37]/[0.04]"
                                    : "border-white/[0.08] bg-white/[0.01] hover:border-white/15"
                            }`}
                        >
                            <input
                                type="checkbox"
                                disabled={busy}
                                checked={active}
                                onChange={() => toggle(key)}
                                className="sr-only"
                            />
                            <div className="flex items-start gap-3 mb-3">
                                <Icon
                                    strokeWidth={1.2}
                                    size={18}
                                    className={
                                        active
                                            ? "text-[#d4af37]"
                                            : "text-[#f4ecd8]/40"
                                    }
                                />
                                <div className="flex-1">
                                    <div
                                        className={`font-serif text-[15px] leading-tight ${
                                            active
                                                ? "text-[#f4ecd8]"
                                                : "text-[#f4ecd8]/70"
                                        }`}
                                    >
                                        {meta.label}
                                    </div>
                                </div>
                                <div
                                    className={`w-9 h-5 rounded-full relative transition-colors duration-500 ${
                                        active
                                            ? "bg-[#d4af37]/80"
                                            : "bg-white/10"
                                    }`}
                                >
                                    <span
                                        className={`absolute top-0.5 w-4 h-4 rounded-full bg-[#070d18] transition-all duration-500 ${
                                            active ? "left-4" : "left-0.5"
                                        }`}
                                    />
                                </div>
                            </div>
                            <p className="text-[11.5px] text-[#f4ecd8]/45 leading-relaxed italic">
                                {meta.description}
                            </p>
                            {active && count > 0 && (
                                <div className="mt-3 text-[10px] tracking-[0.26em] uppercase text-[#d4af37]/70">
                                    {count} entrée{count > 1 ? "s" : ""}
                                </div>
                            )}
                        </label>
                    );
                })}
            </div>

            {/* Module managers — one per enabled */}
            <div className="space-y-10">
                {state.modules_enabled.opportunities && (
                    <OpportunitiesManager dossierId={dossierId} onMutate={reload} />
                )}
                {state.modules_enabled.committee && (
                    <CommitteeManager dossierId={dossierId} onMutate={reload} />
                )}
                {state.modules_enabled.alerts && (
                    <AlertsManager dossierId={dossierId} onMutate={reload} />
                )}
                {state.modules_enabled.questions && (
                    <QuestionsManager dossierId={dossierId} onMutate={reload} />
                )}
                {state.modules_enabled.value_added && (
                    <ValueAddedManager dossierId={dossierId} onMutate={reload} />
                )}
                {state.modules_enabled.documents && (
                    <DocumentsManager dossierId={dossierId} onMutate={reload} />
                )}
                {state.modules_enabled.pipeline && (
                    <PipelineManager dossierId={dossierId} />
                )}
            </div>
        </section>
    );
}

// ─────────────────────────────────────────────────────────────
// Generic helpers
// ─────────────────────────────────────────────────────────────
function ModuleShell({ icon: Icon, title, children, testid }) {
    const [open, setOpen] = useState(true);
    return (
        <div
            data-testid={testid}
            className="border border-[#d4af37]/15 bg-[#070d18]"
        >
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="w-full flex items-center gap-3 px-6 py-5 hover:bg-white/[0.02] transition-colors duration-500"
            >
                <Icon strokeWidth={1.2} size={16} className="text-[#d4af37]" />
                <div
                    className="font-serif text-[18px] text-[#f4ecd8] flex-1 text-left"
                    style={{ fontWeight: 400 }}
                >
                    {title}
                </div>
                {open ? (
                    <ChevronUp strokeWidth={1.3} size={14} className="text-[#d4af37]/70" />
                ) : (
                    <ChevronDown strokeWidth={1.3} size={14} className="text-[#d4af37]/70" />
                )}
            </button>
            {open && (
                <div className="px-6 pb-6 pt-2 border-t border-white/[0.06]">{children}</div>
            )}
        </div>
    );
}

function FieldInput({ label, value, onChange, placeholder, type = "text", testid }) {
    return (
        <label className="block">
            <span className="block text-[10px] tracking-[0.28em] uppercase text-[#d4af37]/70 mb-2">
                {label}
            </span>
            <input
                type={type}
                value={value || ""}
                onChange={onChange}
                placeholder={placeholder}
                data-testid={testid}
                className="w-full bg-transparent border border-white/15 focus:border-[#d4af37] outline-none px-3 py-2 text-[14px] font-serif text-[#f4ecd8]"
            />
        </label>
    );
}

function FieldTextarea({ label, value, onChange, placeholder, rows = 3, testid }) {
    return (
        <label className="block">
            <span className="block text-[10px] tracking-[0.28em] uppercase text-[#d4af37]/70 mb-2">
                {label}
            </span>
            <textarea
                value={value || ""}
                onChange={onChange}
                rows={rows}
                placeholder={placeholder}
                data-testid={testid}
                className="w-full bg-transparent border border-white/15 focus:border-[#d4af37] outline-none p-3 text-[14px] font-serif text-[#f4ecd8] resize-none"
            />
        </label>
    );
}

function FieldSelect({ label, value, onChange, options, testid }) {
    return (
        <label className="block">
            <span className="block text-[10px] tracking-[0.28em] uppercase text-[#d4af37]/70 mb-2">
                {label}
            </span>
            <select
                value={value || ""}
                onChange={onChange}
                data-testid={testid}
                className="w-full bg-[#070d18] border border-white/15 focus:border-[#d4af37] outline-none px-3 py-2 text-[14px] font-serif text-[#f4ecd8]"
            >
                {options.map((o) => (
                    <option key={o.v} value={o.v}>
                        {o.l}
                    </option>
                ))}
            </select>
        </label>
    );
}

function ListField({ label, values, onChange, placeholder, testid }) {
    const set = (i, v) => {
        const next = [...(values || [])];
        next[i] = v;
        onChange(next);
    };
    const add = () => onChange([...(values || []), ""]);
    const rm = (i) => onChange((values || []).filter((_, j) => j !== i));
    return (
        <div data-testid={testid}>
            <span className="block text-[10px] tracking-[0.28em] uppercase text-[#d4af37]/70 mb-2">
                {label}
            </span>
            <ul className="space-y-2">
                {(values || []).map((v, i) => (
                    <li key={i} className="flex gap-2">
                        <input
                            value={v}
                            onChange={(e) => set(i, e.target.value)}
                            placeholder={placeholder}
                            className="flex-1 bg-transparent border border-white/15 focus:border-[#d4af37] outline-none px-3 py-2 text-[13px] font-serif text-[#f4ecd8]"
                        />
                        <button
                            type="button"
                            onClick={() => rm(i)}
                            className="text-[#f4ecd8]/30 hover:text-[#e89090] px-2"
                        >
                            <Trash2 strokeWidth={1.3} size={14} />
                        </button>
                    </li>
                ))}
            </ul>
            <button
                type="button"
                onClick={add}
                className="mt-2 text-[10px] tracking-[0.26em] uppercase text-[#d4af37]/80 hover:text-[#d4af37]"
            >
                + Ajouter
            </button>
        </div>
    );
}

function SaveDeleteRow({ onSave, onCancel, onDelete, saving, testid }) {
    return (
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/[0.06]">
            {onDelete ? (
                <button
                    type="button"
                    onClick={onDelete}
                    data-testid={`${testid}-delete`}
                    className="text-[10px] tracking-[0.26em] uppercase text-[#e89090]/70 hover:text-[#e89090]"
                >
                    Supprimer
                </button>
            ) : (
                <span />
            )}
            <div className="flex gap-3">
                {onCancel && (
                    <button
                        type="button"
                        onClick={onCancel}
                        className="px-4 py-2 text-[10px] tracking-[0.26em] uppercase text-[#f4ecd8]/55 hover:text-[#f4ecd8] border border-white/10"
                    >
                        Annuler
                    </button>
                )}
                <button
                    type="button"
                    onClick={onSave}
                    disabled={saving}
                    data-testid={`${testid}-save`}
                    className="px-5 py-2 text-[10px] tracking-[0.28em] uppercase border border-[#d4af37]/50 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500 disabled:opacity-40"
                >
                    {saving ? "…" : "Enregistrer"}
                </button>
            </div>
        </div>
    );
}

function AISuggestButton({ dossierId, module, onSuggestion, label = "Suggérer une analyse XV" }) {
    const [busy, setBusy] = useState(false);
    const ask = async () => {
        setBusy(true);
        try {
            const { data } = await cockpit.post(
                `/dossiers/${dossierId}/modules/${module}/ai-suggest`,
                { context: "" },
            );
            onSuggestion(data.suggestion || {});
        } catch (e) {
            alert(e?.response?.data?.detail || "Suggestion IA indisponible");
        } finally {
            setBusy(false);
        }
    };
    return (
        <button
            type="button"
            onClick={ask}
            disabled={busy}
            data-testid={`module-${module}-ai-suggest`}
            className="inline-flex items-center gap-2 px-4 py-2 text-[10px] tracking-[0.26em] uppercase border border-[#d4af37]/40 text-[#d4af37]/90 hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500 disabled:opacity-50"
        >
            {busy ? (
                <Loader2 strokeWidth={1.5} size={12} className="animate-spin" />
            ) : (
                <Sparkles strokeWidth={1.3} size={12} />
            )}
            {label}
        </button>
    );
}

// ─────────────────────────────────────────────────────────────
// OPPORTUNITIES
// ─────────────────────────────────────────────────────────────
function OpportunitiesManager({ dossierId, onMutate }) {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null); // null | {id?,...}

    const reload = async () => {
        const { data } = await cockpit.get(
            `/dossiers/${dossierId}/modules/opportunities`,
        );
        setItems(data.items || []);
    };
    useEffect(() => { reload(); }, [dossierId]);

    const save = async () => {
        const payload = {
            name: editing.name || "",
            status: editing.status || "",
            priority: editing.priority || "",
            opened_at: editing.opened_at || "",
            why_studied: editing.why_studied || "",
            potential_value: editing.potential_value || "",
            risks: editing.risks || "",
            actions_done: editing.actions_done || "",
            next_steps: editing.next_steps || "",
            decision: editing.decision || "en_etude",
            internal_notes: editing.internal_notes || "",
        };
        if (editing.id) {
            await cockpit.put(`/dossiers/${dossierId}/modules/opportunities/${editing.id}`, payload);
        } else {
            await cockpit.post(`/dossiers/${dossierId}/modules/opportunities`, payload);
        }
        setEditing(null);
        await reload();
        onMutate?.();
    };

    const del = async (id) => {
        if (!window.confirm("Supprimer cette opportunité ?")) return;
        await cockpit.delete(`/dossiers/${dossierId}/modules/opportunities/${id}`);
        setEditing(null);
        await reload();
        onMutate?.();
    };

    return (
        <ModuleShell icon={Lightbulb} title="Opportunités en étude" testid="module-opportunities">
            <div className="flex items-center justify-between mb-4">
                <span className="text-[11px] text-[#f4ecd8]/50 italic">
                    {items.length} opportunité{items.length > 1 ? "s" : ""} en étude
                </span>
                <div className="flex gap-2">
                    <AISuggestButton
                        dossierId={dossierId}
                        module="opportunities"
                        onSuggestion={(s) => setEditing({ ...(s || {}), decision: "en_etude" })}
                    />
                    <button
                        type="button"
                        onClick={() => setEditing({ decision: "en_etude" })}
                        data-testid="opportunity-new"
                        className="px-4 py-2 text-[10px] tracking-[0.26em] uppercase border border-white/15 text-[#f4ecd8]/75 hover:text-[#f4ecd8]"
                    >
                        + Nouvelle
                    </button>
                </div>
            </div>

            <ul className="space-y-3">
                {items.map((o) => (
                    <li
                        key={o.id}
                        data-testid={`opportunity-${o.id}`}
                        className="border border-white/[0.06] bg-white/[0.02] px-4 py-3 cursor-pointer hover:bg-white/[0.04]"
                        onClick={() => setEditing(o)}
                    >
                        <div className="flex items-baseline justify-between flex-wrap gap-2">
                            <div className="font-serif text-[15px] text-[#f4ecd8]">{o.name}</div>
                            <span className="text-[10px] tracking-[0.26em] uppercase text-[#d4af37]/80">
                                {DECISION_OPTIONS_OP.find((d) => d.v === o.decision)?.l || o.decision}
                            </span>
                        </div>
                        {o.why_studied && (
                            <p className="text-[12px] text-[#f4ecd8]/55 mt-1 line-clamp-2">
                                {o.why_studied}
                            </p>
                        )}
                    </li>
                ))}
            </ul>

            {editing && (
                <div className="mt-6 border-l-2 border-[#d4af37]/40 pl-5 space-y-4">
                    <FieldInput
                        label="Nom du projet *"
                        value={editing.name}
                        onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                        testid="opportunity-name"
                    />
                    <div className="grid sm:grid-cols-3 gap-4">
                        <FieldInput
                            label="Statut"
                            value={editing.status}
                            onChange={(e) => setEditing({ ...editing, status: e.target.value })}
                        />
                        <FieldSelect
                            label="Priorité"
                            value={editing.priority}
                            onChange={(e) => setEditing({ ...editing, priority: e.target.value })}
                            options={PRIORITY_OPTIONS}
                        />
                        <FieldInput
                            label="Date d'ouverture"
                            type="date"
                            value={editing.opened_at}
                            onChange={(e) => setEditing({ ...editing, opened_at: e.target.value })}
                        />
                    </div>
                    <FieldTextarea
                        label="Pourquoi cette opportunité est étudiée"
                        value={editing.why_studied}
                        onChange={(e) => setEditing({ ...editing, why_studied: e.target.value })}
                    />
                    <FieldInput
                        label="Valeur potentielle"
                        value={editing.potential_value}
                        onChange={(e) => setEditing({ ...editing, potential_value: e.target.value })}
                    />
                    <FieldTextarea
                        label="Risques identifiés"
                        value={editing.risks}
                        onChange={(e) => setEditing({ ...editing, risks: e.target.value })}
                    />
                    <FieldTextarea
                        label="Actions réalisées"
                        value={editing.actions_done}
                        onChange={(e) => setEditing({ ...editing, actions_done: e.target.value })}
                    />
                    <FieldTextarea
                        label="Prochaines étapes"
                        value={editing.next_steps}
                        onChange={(e) => setEditing({ ...editing, next_steps: e.target.value })}
                    />
                    <FieldSelect
                        label="Décision XV"
                        value={editing.decision}
                        onChange={(e) => setEditing({ ...editing, decision: e.target.value })}
                        options={DECISION_OPTIONS_OP}
                    />
                    <FieldTextarea
                        label="Notes internes (jamais visibles côté client)"
                        value={editing.internal_notes}
                        onChange={(e) => setEditing({ ...editing, internal_notes: e.target.value })}
                    />
                    <SaveDeleteRow
                        onSave={save}
                        onCancel={() => setEditing(null)}
                        onDelete={editing.id ? () => del(editing.id) : null}
                        testid="opportunity-form"
                    />
                </div>
            )}
        </ModuleShell>
    );
}

// ─────────────────────────────────────────────────────────────
// COMMITTEE
// ─────────────────────────────────────────────────────────────
function CommitteeManager({ dossierId, onMutate }) {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);

    const reload = async () => {
        const { data } = await cockpit.get(`/dossiers/${dossierId}/modules/committee`);
        setItems(data.items || []);
    };
    useEffect(() => { reload(); }, [dossierId]);

    const save = async () => {
        const payload = {
            title: editing.title || "Note de comité",
            date: editing.date || "",
            avis: editing.avis || "",
            forces: (editing.forces || []).filter((s) => s.trim()),
            faiblesses: (editing.faiblesses || []).filter((s) => s.trim()),
            opportunites: (editing.opportunites || []).filter((s) => s.trim()),
            risques: (editing.risques || []).filter((s) => s.trim()),
            recommandation: editing.recommandation || "",
            decision: editing.decision || "",
            internal_notes: editing.internal_notes || "",
        };
        if (editing.id) {
            await cockpit.put(`/dossiers/${dossierId}/modules/committee/${editing.id}`, payload);
        } else {
            await cockpit.post(`/dossiers/${dossierId}/modules/committee`, payload);
        }
        setEditing(null);
        await reload();
        onMutate?.();
    };

    const del = async (id) => {
        if (!window.confirm("Supprimer cette note de comité ?")) return;
        await cockpit.delete(`/dossiers/${dossierId}/modules/committee/${id}`);
        setEditing(null);
        await reload();
        onMutate?.();
    };

    return (
        <ModuleShell icon={Star} title="Comité d'investissement XV" testid="module-committee">
            <div className="flex items-center justify-between mb-4">
                <span className="text-[11px] text-[#f4ecd8]/50 italic">
                    {items.length} note{items.length > 1 ? "s" : ""} de comité
                </span>
                <div className="flex gap-2">
                    <AISuggestButton
                        dossierId={dossierId}
                        module="committee"
                        onSuggestion={(s) => setEditing({ ...(s || {}), title: s?.title || "Note de comité" })}
                    />
                    <button
                        type="button"
                        onClick={() => setEditing({ forces: [], faiblesses: [], opportunites: [], risques: [] })}
                        data-testid="committee-new"
                        className="px-4 py-2 text-[10px] tracking-[0.26em] uppercase border border-white/15 text-[#f4ecd8]/75 hover:text-[#f4ecd8]"
                    >
                        + Nouvelle note
                    </button>
                </div>
            </div>

            <ul className="space-y-3">
                {items.map((n) => (
                    <li
                        key={n.id}
                        data-testid={`committee-${n.id}`}
                        className="border border-white/[0.06] bg-white/[0.02] px-4 py-3 cursor-pointer hover:bg-white/[0.04]"
                        onClick={() => setEditing(n)}
                    >
                        <div className="flex items-baseline justify-between gap-3">
                            <div className="font-serif text-[15px] text-[#f4ecd8]">{n.title}</div>
                            {n.decision && (
                                <span className="text-[10px] tracking-[0.26em] uppercase text-[#d4af37]/80">
                                    {DECISION_OPTIONS_COMITE.find((d) => d.v === n.decision)?.l}
                                </span>
                            )}
                        </div>
                        {n.recommandation && (
                            <p className="text-[12px] text-[#f4ecd8]/55 mt-1 line-clamp-2">
                                {n.recommandation}
                            </p>
                        )}
                    </li>
                ))}
            </ul>

            {editing && (
                <div className="mt-6 border-l-2 border-[#d4af37]/40 pl-5 space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <FieldInput
                            label="Titre *"
                            value={editing.title}
                            onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                            testid="committee-title"
                        />
                        <FieldInput
                            label="Date"
                            type="date"
                            value={editing.date}
                            onChange={(e) => setEditing({ ...editing, date: e.target.value })}
                        />
                    </div>
                    <FieldTextarea
                        label="Avis XV"
                        value={editing.avis}
                        onChange={(e) => setEditing({ ...editing, avis: e.target.value })}
                    />
                    <div className="grid sm:grid-cols-2 gap-4">
                        <ListField
                            label="Forces"
                            values={editing.forces}
                            onChange={(forces) => setEditing({ ...editing, forces })}
                            placeholder="Force identifiée…"
                        />
                        <ListField
                            label="Faiblesses"
                            values={editing.faiblesses}
                            onChange={(faiblesses) => setEditing({ ...editing, faiblesses })}
                            placeholder="Faiblesse identifiée…"
                        />
                        <ListField
                            label="Opportunités"
                            values={editing.opportunites}
                            onChange={(opportunites) => setEditing({ ...editing, opportunites })}
                            placeholder="Opportunité identifiée…"
                        />
                        <ListField
                            label="Risques"
                            values={editing.risques}
                            onChange={(risques) => setEditing({ ...editing, risques })}
                            placeholder="Risque identifié…"
                        />
                    </div>
                    <FieldTextarea
                        label="Recommandation"
                        value={editing.recommandation}
                        onChange={(e) => setEditing({ ...editing, recommandation: e.target.value })}
                    />
                    <FieldSelect
                        label="Décision"
                        value={editing.decision}
                        onChange={(e) => setEditing({ ...editing, decision: e.target.value })}
                        options={DECISION_OPTIONS_COMITE}
                    />
                    <FieldTextarea
                        label="Notes internes (jamais visibles côté client)"
                        value={editing.internal_notes}
                        onChange={(e) => setEditing({ ...editing, internal_notes: e.target.value })}
                    />
                    <SaveDeleteRow
                        onSave={save}
                        onCancel={() => setEditing(null)}
                        onDelete={editing.id ? () => del(editing.id) : null}
                        testid="committee-form"
                    />
                </div>
            )}
        </ModuleShell>
    );
}

// ─────────────────────────────────────────────────────────────
// ALERTS
// ─────────────────────────────────────────────────────────────
function AlertsManager({ dossierId, onMutate }) {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);

    const reload = async () => {
        const { data } = await cockpit.get(`/dossiers/${dossierId}/modules/alerts`);
        setItems(data.items || []);
    };
    useEffect(() => { reload(); }, [dossierId]);

    const save = async () => {
        const payload = {
            level: editing.level || "orange",
            description: editing.description || "",
            consequences: editing.consequences || "",
            actions_xv: editing.actions_xv || "",
        };
        if (!payload.description) return;
        if (editing.id) {
            await cockpit.put(`/dossiers/${dossierId}/modules/alerts/${editing.id}`, payload);
        } else {
            await cockpit.post(`/dossiers/${dossierId}/modules/alerts`, payload);
        }
        setEditing(null);
        await reload();
        onMutate?.();
    };

    const del = async (id) => {
        if (!window.confirm("Supprimer cette alerte ?")) return;
        await cockpit.delete(`/dossiers/${dossierId}/modules/alerts/${id}`);
        setEditing(null);
        await reload();
        onMutate?.();
    };

    return (
        <ModuleShell icon={AlertTriangle} title="Alertes stratégiques" testid="module-alerts">
            <div className="flex items-center justify-between mb-4">
                <span className="text-[11px] text-[#f4ecd8]/50 italic">
                    {items.length} alerte{items.length > 1 ? "s" : ""} active{items.length > 1 ? "s" : ""}
                </span>
                <div className="flex gap-2">
                    <AISuggestButton
                        dossierId={dossierId}
                        module="alerts"
                        onSuggestion={(s) => setEditing({ ...(s || {}), level: s?.level || "orange" })}
                    />
                    <button
                        type="button"
                        onClick={() => setEditing({ level: "orange" })}
                        data-testid="alert-new"
                        className="px-4 py-2 text-[10px] tracking-[0.26em] uppercase border border-white/15 text-[#f4ecd8]/75 hover:text-[#f4ecd8]"
                    >
                        + Nouvelle
                    </button>
                </div>
            </div>

            <ul className="space-y-3">
                {items.map((a) => {
                    const lvl = ALERT_LEVELS.find((l) => l.v === a.level) || ALERT_LEVELS[1];
                    return (
                        <li
                            key={a.id}
                            data-testid={`alert-${a.id}`}
                            className="flex items-start gap-3 border border-white/[0.06] bg-white/[0.02] px-4 py-3 cursor-pointer hover:bg-white/[0.04]"
                            onClick={() => setEditing(a)}
                        >
                            <span
                                className="mt-1.5 w-2.5 h-2.5 rounded-full shrink-0"
                                style={{ background: lvl.color }}
                            />
                            <div className="flex-1">
                                <div className="font-serif text-[14.5px] text-[#f4ecd8]">{a.description}</div>
                                {a.consequences && (
                                    <p className="text-[12px] text-[#f4ecd8]/55 mt-1">
                                        Conséquence · {a.consequences}
                                    </p>
                                )}
                                {a.actions_xv && (
                                    <p className="text-[12px] text-[#d4af37]/75 mt-1">
                                        Action XV · {a.actions_xv}
                                    </p>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ul>

            {editing && (
                <div className="mt-6 border-l-2 border-[#d4af37]/40 pl-5 space-y-4">
                    <FieldSelect
                        label="Niveau"
                        value={editing.level}
                        onChange={(e) => setEditing({ ...editing, level: e.target.value })}
                        options={ALERT_LEVELS.map((l) => ({ v: l.v, l: l.l }))}
                        testid="alert-level"
                    />
                    <FieldTextarea
                        label="Description *"
                        value={editing.description}
                        onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                        testid="alert-description"
                    />
                    <FieldTextarea
                        label="Conséquences potentielles"
                        value={editing.consequences}
                        onChange={(e) => setEditing({ ...editing, consequences: e.target.value })}
                    />
                    <FieldTextarea
                        label="Actions engagées par XV"
                        value={editing.actions_xv}
                        onChange={(e) => setEditing({ ...editing, actions_xv: e.target.value })}
                    />
                    <SaveDeleteRow
                        onSave={save}
                        onCancel={() => setEditing(null)}
                        onDelete={editing.id ? () => del(editing.id) : null}
                        testid="alert-form"
                    />
                </div>
            )}
        </ModuleShell>
    );
}

// ─────────────────────────────────────────────────────────────
// QUESTIONS
// ─────────────────────────────────────────────────────────────
function QuestionsManager({ dossierId, onMutate }) {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);

    const reload = async () => {
        const { data } = await cockpit.get(`/dossiers/${dossierId}/modules/questions`);
        setItems(data.items || []);
    };
    useEffect(() => { reload(); }, [dossierId]);

    const save = async () => {
        const payload = {
            question: editing.question || "",
            options: (editing.options || []).filter((s) => s.trim()),
            xv_analysis: editing.xv_analysis || "",
        };
        if (!payload.question) return;
        if (editing.id) {
            await cockpit.put(`/dossiers/${dossierId}/modules/questions/${editing.id}`, payload);
        } else {
            await cockpit.post(`/dossiers/${dossierId}/modules/questions`, payload);
        }
        setEditing(null);
        await reload();
        onMutate?.();
    };

    const del = async (id) => {
        if (!window.confirm("Supprimer cette question ?")) return;
        await cockpit.delete(`/dossiers/${dossierId}/modules/questions/${id}`);
        setEditing(null);
        await reload();
        onMutate?.();
    };

    return (
        <ModuleShell icon={HelpCircle} title="Questions stratégiques à l'étude" testid="module-questions">
            <div className="flex items-center justify-between mb-4">
                <span className="text-[11px] text-[#f4ecd8]/50 italic">
                    {items.length} arbitrage{items.length > 1 ? "s" : ""} en cours
                </span>
                <div className="flex gap-2">
                    <AISuggestButton
                        dossierId={dossierId}
                        module="questions"
                        onSuggestion={(s) => setEditing({ ...(s || {}) })}
                    />
                    <button
                        type="button"
                        onClick={() => setEditing({ options: [] })}
                        data-testid="question-new"
                        className="px-4 py-2 text-[10px] tracking-[0.26em] uppercase border border-white/15 text-[#f4ecd8]/75 hover:text-[#f4ecd8]"
                    >
                        + Nouvelle
                    </button>
                </div>
            </div>

            <ul className="space-y-3">
                {items.map((q) => (
                    <li
                        key={q.id}
                        data-testid={`question-${q.id}`}
                        className="border border-white/[0.06] bg-white/[0.02] px-4 py-3 cursor-pointer hover:bg-white/[0.04]"
                        onClick={() => setEditing(q)}
                    >
                        <div className="font-serif text-[15px] text-[#f4ecd8]">{q.question}</div>
                        {(q.options || []).length > 0 && (
                            <p className="text-[12px] text-[#f4ecd8]/55 mt-1">
                                {(q.options || []).join(" · ")}
                            </p>
                        )}
                    </li>
                ))}
            </ul>

            {editing && (
                <div className="mt-6 border-l-2 border-[#d4af37]/40 pl-5 space-y-4">
                    <FieldTextarea
                        label="Question stratégique *"
                        value={editing.question}
                        onChange={(e) => setEditing({ ...editing, question: e.target.value })}
                        rows={2}
                        testid="question-text"
                    />
                    <ListField
                        label="Options en arbitrage"
                        values={editing.options}
                        onChange={(options) => setEditing({ ...editing, options })}
                        placeholder="Equity, Dette privée, SPV…"
                    />
                    <FieldTextarea
                        label="Analyse XV"
                        value={editing.xv_analysis}
                        onChange={(e) => setEditing({ ...editing, xv_analysis: e.target.value })}
                        rows={4}
                    />
                    <SaveDeleteRow
                        onSave={save}
                        onCancel={() => setEditing(null)}
                        onDelete={editing.id ? () => del(editing.id) : null}
                        testid="question-form"
                    />
                </div>
            )}
        </ModuleShell>
    );
}

// ─────────────────────────────────────────────────────────────
// VALUE ADDED
// ─────────────────────────────────────────────────────────────
function ValueAddedManager({ dossierId, onMutate }) {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);

    const reload = async () => {
        const { data } = await cockpit.get(`/dossiers/${dossierId}/modules/value_added`);
        setItems(data.items || []);
    };
    useEffect(() => { reload(); }, [dossierId]);

    const save = async () => {
        const payload = {
            date: editing.date || "",
            category: editing.category || "other",
            title: editing.title || "",
            description: editing.description || "",
            internal_notes: editing.internal_notes || "",
        };
        if (!payload.title) return;
        if (editing.id) {
            await cockpit.put(`/dossiers/${dossierId}/modules/value_added/${editing.id}`, payload);
        } else {
            await cockpit.post(`/dossiers/${dossierId}/modules/value_added`, payload);
        }
        setEditing(null);
        await reload();
        onMutate?.();
    };

    const del = async (id) => {
        if (!window.confirm("Supprimer cet apport ?")) return;
        await cockpit.delete(`/dossiers/${dossierId}/modules/value_added/${id}`);
        setEditing(null);
        await reload();
        onMutate?.();
    };

    return (
        <ModuleShell icon={CheckCircle2} title="Valeur ajoutée créée par XV" testid="module-value-added">
            <div className="flex items-center justify-between mb-4">
                <span className="text-[11px] text-[#f4ecd8]/50 italic">
                    {items.length} apport{items.length > 1 ? "s" : ""}
                </span>
                <div className="flex gap-2">
                    <AISuggestButton
                        dossierId={dossierId}
                        module="value_added"
                        onSuggestion={(s) => setEditing({ ...(s || {}), category: s?.category || "other" })}
                    />
                    <button
                        type="button"
                        onClick={() => setEditing({ category: "other" })}
                        data-testid="value-new"
                        className="px-4 py-2 text-[10px] tracking-[0.26em] uppercase border border-white/15 text-[#f4ecd8]/75 hover:text-[#f4ecd8]"
                    >
                        + Nouvel apport
                    </button>
                </div>
            </div>

            <ul className="space-y-3">
                {items.map((v) => (
                    <li
                        key={v.id}
                        data-testid={`value-${v.id}`}
                        className="border border-white/[0.06] bg-white/[0.02] px-4 py-3 cursor-pointer hover:bg-white/[0.04]"
                        onClick={() => setEditing(v)}
                    >
                        <div className="flex items-baseline justify-between gap-3 flex-wrap">
                            <div className="font-serif text-[15px] text-[#f4ecd8]">{v.title}</div>
                            <span className="text-[10px] tracking-[0.26em] uppercase text-[#d4af37]/70">
                                {VALUE_CATEGORIES.find((c) => c.v === v.category)?.l || v.category}
                            </span>
                        </div>
                        {v.description && (
                            <p className="text-[12px] text-[#f4ecd8]/55 mt-1 line-clamp-2">
                                {v.description}
                            </p>
                        )}
                    </li>
                ))}
            </ul>

            {editing && (
                <div className="mt-6 border-l-2 border-[#d4af37]/40 pl-5 space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <FieldInput
                            label="Titre *"
                            value={editing.title}
                            onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                            testid="value-title"
                        />
                        <FieldInput
                            label="Date"
                            type="date"
                            value={editing.date}
                            onChange={(e) => setEditing({ ...editing, date: e.target.value })}
                        />
                    </div>
                    <FieldSelect
                        label="Catégorie"
                        value={editing.category}
                        onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                        options={VALUE_CATEGORIES}
                    />
                    <FieldTextarea
                        label="Description"
                        value={editing.description}
                        onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                    />
                    <FieldTextarea
                        label="Notes internes (jamais visibles côté client)"
                        value={editing.internal_notes}
                        onChange={(e) => setEditing({ ...editing, internal_notes: e.target.value })}
                    />
                    <SaveDeleteRow
                        onSave={save}
                        onCancel={() => setEditing(null)}
                        onDelete={editing.id ? () => del(editing.id) : null}
                        testid="value-form"
                    />
                </div>
            )}
        </ModuleShell>
    );
}

// ─────────────────────────────────────────────────────────────
// DOCUMENTS CHECKLIST
// ─────────────────────────────────────────────────────────────
function DocumentsManager({ dossierId, onMutate }) {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);

    const reload = async () => {
        const { data } = await cockpit.get(`/dossiers/${dossierId}/modules/documents`);
        setItems(data.items || []);
    };
    useEffect(() => { reload(); }, [dossierId]);

    const save = async () => {
        const payload = {
            name: editing.name || "",
            status: editing.status || "expected",
            category: editing.category || "",
            note: editing.note || "",
        };
        if (!payload.name) return;
        if (editing.id) {
            await cockpit.put(`/dossiers/${dossierId}/modules/documents/${editing.id}`, payload);
        } else {
            await cockpit.post(`/dossiers/${dossierId}/modules/documents`, payload);
        }
        setEditing(null);
        await reload();
        onMutate?.();
    };

    const quickToggle = async (item) => {
        const next = item.status === "received" ? "expected" : "received";
        await cockpit.put(`/dossiers/${dossierId}/modules/documents/${item.id}`, {
            name: item.name,
            status: next,
            category: item.category || "",
            note: item.note || "",
        });
        await reload();
        onMutate?.();
    };

    const del = async (id) => {
        if (!window.confirm("Supprimer ce document ?")) return;
        await cockpit.delete(`/dossiers/${dossierId}/modules/documents/${id}`);
        setEditing(null);
        await reload();
        onMutate?.();
    };

    const received = items.filter((d) => d.status === "received");
    const expected = items.filter((d) => d.status === "expected");

    return (
        <ModuleShell icon={ClipboardList} title="Documents attendus" testid="module-documents">
            <div className="flex items-center justify-between mb-4">
                <span className="text-[11px] text-[#f4ecd8]/50 italic">
                    {received.length} reçu{received.length > 1 ? "s" : ""} ·{" "}
                    {expected.length} attendu{expected.length > 1 ? "s" : ""}
                </span>
                <button
                    type="button"
                    onClick={() => setEditing({ status: "expected" })}
                    data-testid="document-new"
                    className="px-4 py-2 text-[10px] tracking-[0.26em] uppercase border border-white/15 text-[#f4ecd8]/75 hover:text-[#f4ecd8]"
                >
                    + Nouveau document
                </button>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
                {[["received", "Reçus"], ["expected", "Attendus"]].map(([key, lbl]) => (
                    <div key={key}>
                        <div className="overline text-[#d4af37]/70 mb-3">{lbl}</div>
                        <ul className="space-y-2">
                            {items.filter((d) => d.status === key).map((d) => {
                                const colors = DOC_STATUSES.find((s) => s.v === d.status);
                                return (
                                    <li
                                        key={d.id}
                                        data-testid={`document-${d.id}`}
                                        className="flex items-center gap-3 border border-white/[0.06] bg-white/[0.02] px-3 py-2"
                                    >
                                        <button
                                            type="button"
                                            onClick={() => quickToggle(d)}
                                            className="w-3 h-3 rounded-full shrink-0"
                                            style={{ background: colors?.color || "#888" }}
                                            title="Basculer reçu / attendu"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setEditing(d)}
                                            className="flex-1 text-left font-serif text-[14px] text-[#f4ecd8]/90"
                                        >
                                            {d.name}
                                            {d.category && (
                                                <span className="ml-2 text-[10px] tracking-[0.22em] uppercase text-[#f4ecd8]/40">
                                                    {d.category}
                                                </span>
                                            )}
                                        </button>
                                    </li>
                                );
                            })}
                            {items.filter((d) => d.status === key).length === 0 && (
                                <li className="text-[11px] italic text-[#f4ecd8]/35">Aucun.</li>
                            )}
                        </ul>
                    </div>
                ))}
            </div>

            {editing && (
                <div className="mt-6 border-l-2 border-[#d4af37]/40 pl-5 space-y-4">
                    <FieldInput
                        label="Nom du document *"
                        value={editing.name}
                        onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                        testid="document-name"
                        placeholder="Bilan, pacte d'associés, organigramme…"
                    />
                    <div className="grid sm:grid-cols-2 gap-4">
                        <FieldSelect
                            label="Statut"
                            value={editing.status}
                            onChange={(e) => setEditing({ ...editing, status: e.target.value })}
                            options={DOC_STATUSES.map((s) => ({ v: s.v, l: s.l }))}
                        />
                        <FieldInput
                            label="Catégorie"
                            value={editing.category}
                            onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                            placeholder="Juridique, financier, RH…"
                        />
                    </div>
                    <FieldTextarea
                        label="Note"
                        value={editing.note}
                        onChange={(e) => setEditing({ ...editing, note: e.target.value })}
                        rows={2}
                    />
                    <SaveDeleteRow
                        onSave={save}
                        onCancel={() => setEditing(null)}
                        onDelete={editing.id ? () => del(editing.id) : null}
                        testid="document-form"
                    />
                </div>
            )}
        </ModuleShell>
    );
}

// ─────────────────────────────────────────────────────────────
// PIPELINE STAGE
// ─────────────────────────────────────────────────────────────
function PipelineManager({ dossierId }) {
    const [data, setData] = useState(null);
    const [saving, setSaving] = useState(false);

    const reload = async () => {
        const { data: d } = await cockpit.get(`/dossiers/${dossierId}/modules/pipeline`);
        setData(d);
    };
    useEffect(() => { reload(); }, [dossierId]);

    const setStage = async (stage) => {
        setSaving(true);
        try {
            await cockpit.put(`/dossiers/${dossierId}/modules/pipeline`, {
                current_stage: stage,
            });
            await reload();
        } finally {
            setSaving(false);
        }
    };

    if (!data) return null;

    return (
        <ModuleShell icon={GitBranch} title="Pipeline stratégique" testid="module-pipeline">
            <p className="text-[12px] text-[#f4ecd8]/50 italic mb-6 leading-relaxed">
                Sélectionnez l'étape courante du pipeline pour ce dossier.
                Le client voit la timeline complète avec l'étape mise en
                évidence.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                {data.stages.map((s) => {
                    const active = data.current_stage === s.key;
                    return (
                        <button
                            key={s.key}
                            type="button"
                            data-testid={`pipeline-stage-${s.key}`}
                            disabled={saving}
                            onClick={() => setStage(active ? "" : s.key)}
                            className={`px-4 py-3 text-[11.5px] tracking-[0.22em] uppercase border transition-all duration-500 ${
                                active
                                    ? "bg-[#d4af37] text-[#070d18] border-[#d4af37]"
                                    : "border-white/15 text-[#f4ecd8]/65 hover:border-[#d4af37]/40 hover:text-[#d4af37]"
                            }`}
                        >
                            {s.label}
                        </button>
                    );
                })}
            </div>
        </ModuleShell>
    );
}
